// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import { IconWrapper } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import LazyImage from "@sc/MobiusOverrides/LazyImage/LazyImage";
import Tooltip, { TooltipPresentationProps } from "@sc/MobiusOverrides/Tooltip/Tooltip";
import { ellipsis } from "@sc/XDL/Utilities";
import * as React from "react";
import styled, { css } from "styled-components";

export enum BadgeTypeValues {
    Image = "Image",
    Text = "Text",
}

export interface BadgePresentationProps {
    textBadgeCss?: InjectableCss;
    imageBadgeProps?: JSX.IntrinsicElements["img"];
    // XNG-CHANGES
    iconStyledWrapper?: InjectableCss;
    imageWrapperStyles?: InjectableCss;
}

export const badgePresentationProps: BadgePresentationProps = {
    iconStyledWrapper: {
        css: css`
            display: flex;
            &&& {
                ${IconWrapper} {
                    width: auto;
                    svg {
                        width: auto;
                    }
                }
            }
        `,
    },
    imageWrapperStyles: {
        css: css`
            width: 23px;
            height: 23px;
        `,
    },
};
const styles = badgePresentationProps;
// xng-changes : add badge custom style
const TextBadge = styled.div<{ backgroundColor: string; color: string; badgeTextRounding: number }>`
    display: flex;
    text-align: center;
    position: relative;
    padding: 3px 8px;
    background-color: #${props => props.backgroundColor};
    color: #${props => props.color};
    border-radius: ${props => props.badgeTextRounding}px;
    line-height: 17px;
    font-size: 10px;
    font-weight: bold;
    ${TypographyStyle} {
        width: 100%;
        overflow: hidden;
        ${ellipsis(1)}
    }
`;

export type BadgeProps = {
    /** When type is Image an img tag will be rendered with the src being the value prop.
     * When the type is text a textbox will be rendered with the given value used as text and color scheme
     * denoted in badgeTextColor and badgeBackgroundColor
     * When no type is given or no corresponding value is given, nothing will render.
     */
    type: BadgeTypeValues;
    /** URL image path or badge text */
    value?: string;
    /** Alt text for the image badge */
    imageAltText?: string;
    /** Hex code for badge text color
     * Default is white (FFFFFF)
     */
    badgeTextColor?: string;
    /** Hex code for badge background color
     * Default is black (000000)
     */
    badgeBackgroundColor?: string;
    /** Number used to determine border radius for text badge in pixels
     * Default is 0
     */
    badgeTextRounding?: number;
} & BadgePresentationProps;

const Badge = ({
    type,
    value,
    imageAltText,
    badgeTextColor,
    badgeBackgroundColor,
    badgeTextRounding,
    imageBadgeProps,
    textBadgeCss,
}: BadgeProps) => {
    if (!type || !value) {
        return null;
    }

    // XNG-CHANGES: Check if the icon is a string or svg
    if (type === BadgeTypeValues.Image) {
        if (typeof value === "string") {
            return (
                <StyledWrapper {...styles.imageWrapperStyles}>
                    <LazyImage width="23" height="23" imgProps={imageBadgeProps} src={value} altText={imageAltText} />
                </StyledWrapper>
            );
        }
        return (
            <StyledWrapper {...styles.iconStyledWrapper}>
                <Icon src={value} height={23} width={23} />
            </StyledWrapper>
        );
    }

    if (type === BadgeTypeValues.Text) {
        return (
            <TextBadge
                backgroundColor={badgeBackgroundColor || "FFFFFF"}
                color={badgeTextColor || "000000"}
                badgeTextRounding={badgeTextRounding || 12}
            >
                <Typography {...textBadgeCss}>{value}</Typography>
            </TextBadge>
        );
    }

    return <>Unknown badge type</>;
};

/** @component */
export default Badge;
